import { Button, CalendarFilledIcon, LoadSpinner } from '@calizahq/react-components-v2'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { WorkspaceTab, WorkspaceView } from '@sales-app/context/workspace/workspace.type'
import { getWorkspacePath } from '@sales-app/context/workspace/workspace.util'
import { FollowUpQueries, FollowUpTask, FollowUpTaskRow } from '@sales-app/graphql/queries/followup/followUp.queries'
import { Status } from '@sales-app/graphql/queries/potentialBooking/status.queries'
import { BookablePersonType } from '@sales-app/types/BookablePersonType'
import { isValid } from 'date-fns'
import React, { FC, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { BottomSheet } from '@/components'
import { useToggle } from '@/hooks'
import { useGraphql } from '@/hooks/useGraphql'
import { formatDate, parseDate } from '@/util'

import { SectionContainer } from '../PotentialBooking'
import { AdjustFollowUpTaskDueDates } from './AdjustFollowUpTaskDueDates'
import { CompleteFollowUpTask } from './CompleteFollowUpTask'
import { CreateFollowup } from './CreateFollowup/CreateFollowup'
import { MarkAsLost } from './MarkAsLost'

type Props = {
  bookablePerson: WorkspaceTab
  potentialBookingStatuses: Status[]
  refetchPotentialBooking: () => void
}

interface Option {
  label: string
  value: number
}

export const FollowupSection: FC<Props> = ({ bookablePerson, potentialBookingStatuses, refetchPotentialBooking }) => {
  const currentDate = new Date()
  const navigate = useNavigate()
  const [completingFollowUpTask, setCompletingFollowUpTask] = useState<FollowUpTask>()
  const [lostPotentialBookingStatus, setLostPotentialBookingStatus] = useState<Option>()

  const { data, refetch, isLoading } = useGraphql<FollowUpQueries.GET.Response>(
    FollowUpQueries.getFollowUpTasks.name,
    FollowUpQueries.getFollowUpTasks(),
    {
      filter: {
        potentialBookingId: bookablePerson.potentialBookingId,
      },
    },
    { enabled: !!bookablePerson.potentialBookingId },
  )

  const lostPotentialBookingStatuses =
    potentialBookingStatuses.filter((potentialBookingStatus) => potentialBookingStatus.lifecycleState === 'Lost') || []

  const options = lostPotentialBookingStatuses.map((status) => {
    return { label: status.description, value: status.id }
  })

  const followUpTasksData = useMemo(() => {
    if (!data?.followUpTasks) return []
    return transformedFollowUpTasks(data.followUpTasks).filter((task) => task.status !== 'cancelled')
  }, [data])

  const { isShown: completeModalShown, toggle: completeModalToggle } = useToggle()
  const { isShown: createModalShown, toggle: createModalToggle } = useToggle()
  const { isShown: lostModalShown, toggle: lostModalToggle } = useToggle()
  const { isShown: adjustingFollowUpTaskDueDateModalShown, toggle: adjustingFollowUpTaskDueDateModalToggle } =
    useToggle()

  const handleOpenCompleteModal = (followUpTask) => {
    completeModalToggle()
    setCompletingFollowUpTask(followUpTask)
  }

  const handleCloseCompleteModal = () => {
    completeModalToggle()
    setCompletingFollowUpTask(undefined)
  }

  const handleOpenLostModal = (lostPotentialBookingStatus) => {
    lostModalToggle()
    setLostPotentialBookingStatus(lostPotentialBookingStatus)
  }

  const handleCloseLostModal = () => {
    lostModalToggle()
    setLostPotentialBookingStatus(undefined)
    const { bookablePersonId, bookablePersonType } = bookablePerson
    const path = getWorkspacePath({
      view: WorkspaceView.SUMMARY,
      type: bookablePersonType,
      id: bookablePersonId,
      potentialBookingId: null,
    })

    navigate(path)
  }

  const nextDueDate = useMemo(() => {
    const incompleteFollowUpTasks = followUpTasksData?.filter(
      (followUpTask) => followUpTask.status !== 'cancelled' && followUpTask.status !== 'completed',
    )
    if (incompleteFollowUpTasks && incompleteFollowUpTasks.length > 0 && incompleteFollowUpTasks[0]) {
      return parseDate(incompleteFollowUpTasks[0].dueDate)
    }
  }, [followUpTasksData])

  return (
    <div className="z-10 flex opacity-100 w-full h-full">
      <div className="flex w-full flex-col">
        {isLoading ? (
          <div className="flex w-full items-center justify-center">
            <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
          </div>
        ) : (
          <>
            {bookablePerson.potentialBookingId && (
              <div className="mb-2 flex w-full items-center justify-between md:mb-4 md:flex-row">
                <div className="hidden items-center md:flex">
                  <Button className="mr-4 bg-action-primary" onClick={createModalToggle} size="small">
                    Create follow-up
                  </Button>
                </div>
              </div>
            )}
            <div className="flex w-full">
              <div className="mb-6 flex w-1/2 flex-col">
                {followUpTasksData.length > 0 && (
                  <SectionContainer className="flex flex-col max-h-[70vh] overflow-y-auto">
                    <h3 className="mb-2 text-header-md">
                      {bookablePerson.bookablePersonType === BookablePersonType.LEAD
                        ? 'Potential New Member Checklist'
                        : 'Member Checklist'}
                    </h3>
                    <div className="flex mb-2 items-center justify-left">
                      <p className="text-header-xs font-bold">Next Due Date </p>
                      <Button
                        className="ml-8 bg-grey-100"
                        onClick={adjustingFollowUpTaskDueDateModalToggle}
                        renderLeftIcon={CalendarFilledIcon}
                        size="small"
                        variant="secondary"
                      >
                        {formatDate(nextDueDate, 'MMMM dd, yyyy')}
                      </Button>
                    </div>
                    {followUpTasksData.map((followUpTask) => {
                      return (
                        <div className="flex md:items-left" key={followUpTask.id}>
                          <div
                            onClick={
                              followUpTask.followUpTaskStatus == 'Completed' ||
                              followUpTask.followUpTaskStatus == 'Cancelled' ||
                              (followUpTask.formattedDueDate && followUpTask.formattedDueDate > currentDate)
                                ? undefined
                                : () => handleOpenCompleteModal(followUpTask)
                            }
                            role="presentation"
                          >
                            <Radio
                              checked={followUpTask.followUpTaskStatus == 'Completed'}
                              className="shrink-0"
                              disabled={
                                (followUpTask.formattedDueDate && followUpTask.formattedDueDate > currentDate) ||
                                followUpTask.followUpTaskStatus == 'Cancelled'
                              }
                              isError={false}
                            />
                          </div>
                          <div
                            className={`flex items-left pl-1 pb-1 ${
                              followUpTask.followUpTaskStatus == 'Cancelled' ? 'line-through' : ''
                            }`}
                            style={{ wordBreak: 'break-all' }}
                          >
                            <p className="font-bold shrink-0 pr-2">
                              {`${formatDate(parseDate(followUpTask.dueDate), 'MMM. dd')} -`}
                            </p>
                            {followUpTask.followUpTaskType}
                            {followUpTask.notes ? ` (${followUpTask.notes})` : ''}
                          </div>
                        </div>
                      )
                    })}
                  </SectionContainer>
                )}
              </div>
              {!!bookablePerson.potentialBooking?.pending ? (
                <div className="ml-4 mb-6 flex w-1/2 flex-col">
                  <SectionContainer className="flex flex-col max-h-[70vh] overflow-y-auto">
                    <h3 className="mb-2 text-header-md">Mark as Lost</h3>

                    {options.map((option) => {
                      return (
                        <div className="flex md:items-left" key={option.value}>
                          <div onClick={() => handleOpenLostModal(option)} role="presentation">
                            <Radio
                              checked={bookablePerson.potentialBooking?.potentialBookingStatusId == option.value}
                              className="shrink-0"
                              isError={false}
                            />
                          </div>
                          <div className="flex items-left pl-1 pb-1" style={{ wordBreak: 'break-all' }}>
                            <p className="font-bold shrink-0 pr-2">{option.label}</p>
                          </div>
                        </div>
                      )
                    })}
                  </SectionContainer>
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
      <BottomSheet isVisible={createModalShown} onClose={createModalToggle} showClose={false}>
        <CreateFollowup bookablePerson={bookablePerson} refetchOnSuccess={refetch} toggle={createModalToggle} />
      </BottomSheet>
      {nextDueDate && bookablePerson.potentialBookingId && (
        <BottomSheet
          isVisible={adjustingFollowUpTaskDueDateModalShown}
          onClose={adjustingFollowUpTaskDueDateModalToggle}
        >
          <AdjustFollowUpTaskDueDates
            dueDate={nextDueDate}
            potentialBookingId={bookablePerson.potentialBookingId}
            refetchOnSuccess={refetch}
            toggle={adjustingFollowUpTaskDueDateModalToggle}
          />
        </BottomSheet>
      )}
      {completingFollowUpTask && (
        <BottomSheet
          header={`Task ID: ${completingFollowUpTask.id} (${completingFollowUpTask.followUpTaskType})`}
          isVisible={completeModalShown}
          onClose={completeModalToggle}
        >
          <CompleteFollowUpTask
            followUpTask={completingFollowUpTask}
            refetchOnSuccess={refetch}
            toggle={handleCloseCompleteModal}
          />
        </BottomSheet>
      )}
      {lostPotentialBookingStatus && bookablePerson?.potentialBooking?.id && (
        <BottomSheet header={lostPotentialBookingStatus.label} isVisible={lostModalShown} onClose={lostModalToggle}>
          <MarkAsLost
            potentialBookingId={bookablePerson.potentialBooking.id}
            potentialBookingStatusId={lostPotentialBookingStatus.value}
            refetch={refetch}
            refetchPotentialBooking={refetchPotentialBooking}
            toggle={handleCloseLostModal}
          />
        </BottomSheet>
      )}
    </div>
  )
}

const Radio = styled.div<{ checked?: boolean; disabled?: boolean; isError: boolean }>`
  border-color: #e7e6e4;
  border-width: 1px;
  border-style: solid;
  background-color: #ffffff;
  position: relative;
  display: flex;
  margin-right: 1rem;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;

  ${({ checked, disabled }) =>
    checked
      ? css`
          border-width: 1px;
          border-color: transparent;
          background-color: #08786c;
          &::after {
            content: '\\2713';
            color: white;
            position: absolute;
            left: 50%;
            top: 50%;
            font-size: 1.25rem;
            font-weight: 700;
            transform: translateX(-50%) translateY(-50%);
          }
        `
      : disabled
        ? css`
            opacity: 0.4;
            cursor: not-allowed;
          `
        : css`
            border-width: 2px;
            cursor: pointer;
          `}
`

const transformedFollowUpTasks = (followUpTasks: FollowUpTask[]): FollowUpTaskRow[] => {
  return followUpTasks.map((f) => {
    // parseDate(f.dueDate) calls parseISO on the "YYYY-MM-DD" string from the API.
    // The previous double-conversion through formatDate produced Invalid Date and broke
    // status calculation and the future-date click guard.
    const convertedDueDate = parseDate(f.dueDate)
    const currentDate = new Date()

    const getTaskStatus = (f: FollowUpTask) => {
      if (f.followUpTaskStatus === 'Completed') {
        return 'completed'
      }
      if (f.cancelledAt) {
        return 'cancelled'
      }

      if (convertedDueDate && isValid(convertedDueDate)) {
        if (convertedDueDate > currentDate) {
          return 'upcoming'
        } else {
          return 'overdue'
        }
      }
      return ''
    }

    return {
      ...f,
      formattedDueDate: convertedDueDate,
      status: getTaskStatus(f),
    }
  })
}
